import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    inject
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DateDrillDownComponent } from "@components/date-drill-down/date-drill-down.component";
import { FooterComponent } from "@components/footer/footer.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IDateDrillDownSelectedItem } from "~app/common/components/date-drill-down/components/date-drill-down-selector/models/dateDrillDownSelectedItem";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { Expander } from "~app/common/components/hot-doc/hotdoc/expander";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { ToastService } from "~app/common/components/toast/toast.service";
import { AggregatPeriods, IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { ForsorjningskallaAggregat } from "~app/common/models/forsorjningskalla";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FordelaService } from "~app/common/services/fordela.service";
import { SetForsorjningsradPlanLevorderCommand } from "~app/features/justera-leveransplan/common/commands/setForsorjningsradPlanCommands";
import { SetLevorderLeveransradKvantitetCommand } from "~app/features/justera-leveransplan/common/commands/setLevorderLeveransradKvantitetCommand";
import { SetOverenskommenKvantitetLevorderCommand } from "~app/features/justera-leveransplan/common/commands/setOverenskommenKvantitetCommands";
import { LeverantorsorderAggregat, LeverantorsorderradAggregat } from "~app/features/justera-leveransplan/common/models/jlp-leverantorsoder-model";
import { IJlpResultSettings } from "~app/features/justera-leveransplan/common/models/jlp-model";
import { GridPeriod, IOrderLeverantorsorderSearchResponse } from "~app/features/justera-leveransplan/common/models/justeraLeveransplanModel";
import { JlpPeriodService } from "~app/features/justera-leveransplan/common/services/jlp-period.service";
import { HotkeyDirective } from "../../../../../common/directives/hotkey/hotkey.directive";
import { LeverantorsorderForsorjningskallaCell, LeverantorsorderradCell } from "../../../common/services/jlp-leverantorsorder.hot-cells";

/* eslint-disable no-bitwise */
@Component({
    selector: "rask-jlp-leverantorsorder-result",
    templateUrl: "./jlp-leverantorsorder-result.component.html",
    styleUrls: ["./jlp-leverantorsorder-result.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateDrillDownComponent,
        CheckboxComponent,
        HotDocComponent,
        FooterComponent,
        ButtonComponent,
        HotkeyDirective,
        NgbTooltip,
        TranslatePipe
    ]
})
export class JlpLeverantorsorderResultComponent implements OnChanges {
    @Input() canEdit: boolean;
    currentPeriod: IAggregatPeriod = null;
    @Input() data: IOrderLeverantorsorderSearchResponse;
    hotDoc: HotDoc;
    @Input() period: GridPeriod;
    @Input() periodFrom: any;
    @Input() periodTom: any;
    @Input() settings: IJlpResultSettings;
    showTakt = true;

    private readonly $translate = inject(TranslateService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly fordelaKvantitetService = inject(FordelaService);
    private readonly jlpPeriodService = inject(JlpPeriodService);
    private readonly toastService = inject(ToastService);

    private aggregatePeriods: IAggregatPeriod[];
    private model: LeverantorsorderAggregat;

    constructor() {
        const commandService = this.commandService;

        this.commandService = commandService.createInstance();
    }

    canSave(): boolean {
        return this.commandService.isDirty();
    }

    dagarKvarInnevarandeManad(settings: IJlpResultSettings): number {
        return settings.totaltAntalArbetsdagarInnevarandeManad - Math.round(settings.passeradeAntalArbetsdagarInnevarandeManad);
    }

    initHotDoc = () => {
        this.prepareHotDoc(this.data);
        this.hotDoc = this.buildHotDoc();
    };

    isDirty(): boolean {
        return this.commandService.isDirty();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.settings !== undefined) {
            this.settings = changes.settings.currentValue;
        }

        this.initHotDoc();
    }

    onDateSelected(item: IDateDrillDownSelectedItem) {
        this.period = this.jlpPeriodService.onDateSelected(item);
        this.prepareHotDoc(this.data);
        this.hotDoc.rebind();
    }

    save(): void {
        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            .subscribe(() => this.toastService.add("Ändringarna sparades"));
    }

    toggleTakt() {
        this.hotDoc.bind();
    }

    private readonly buildColumnDefinitions = (periods: IAggregatPeriod[]): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];

        columns.push({
            header: "",
            width: 220,
            readOnly: true,
            cellName: "rowHeader1"
        });

        columns.push({
            header: "",
            width: 100,
            readOnly: true,
            cellName: "rowHeader2"
        });

        if (this.showTakt) {
            columns.push({
                header: "Takt",
                width: 60,
                readOnly: true,
                cellName: "takt",
                type: "numeric"
            });
        }

        const periodCols = periods.map(period => ({
            header: period.name,
            width: 95,
            cellName: period.key,
            type: "numeric"
        }));
        columns.push(...periodCols);

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA"),
            cellName: "summa",
            type: "numeric",
            width: 95
        });

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT_LEVERANSRAD"),
            cellName: "transporteratleveransrad",
            type: "numeric",
            width: 140
        });

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_OVERENSKOMMET"),
            cellName: "overenskommet",
            type: "numeric",
            width: 130
        });

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_AVVIKELSE"),
            cellName: "avvikelse",
            type: "numeric",
            width: 95
        });

        return columns;
    };

    private readonly onUpdateForsorjningsradPlan = (model: ForsorjningskallaAggregat, leverantorsorderradId: number, period: IAggregatPeriod, change: NumericCellChangeArg) => {
        const periodStart = period.getStartOrLater(model.periodFrom);
        const periodEnd = period.getEndOrEarlier(model.periodTom);

        this.fordelaKvantitetService
            .getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateForsorjningsradPlan(result);

                const command = new SetForsorjningsradPlanLevorderCommand(c => {
                    c.kundorderradId = model.id;
                    c.leverantorsorderradId = leverantorsorderradId;
                    c.periodStart = periodStart;
                    c.periodEnd = periodEnd;
                    c.fordeladKvantitet = result;
                });
                this.commandService.addTransactionCommand(command);

                change.complete();
            });
    };

    private readonly onUpdateOverenskommenKvantitet = (model: ForsorjningskallaAggregat, leverantorsorderradId: number, change: NumericCellChangeArg) => {
        model.overenskommet = change.newValue;

        const command = new SetOverenskommenKvantitetLevorderCommand(c => {
            c.kundorderradId = model.id;
            c.leverantorsorderradId = leverantorsorderradId;
            c.kvantitet = change.newValue;
        });
        this.commandService.addTransactionCommand(command);

        change.complete();
    };

    private readonly buildForsorjningsradPlanrad = (row: HotRow, model: ForsorjningskallaAggregat, leverantorsorderradId: number, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.White | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.add("rowHeader1", LeverantorsorderForsorjningskallaCell.create(model));
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_PLAN"), Cellstyle.Readonly | Cellstyle.White | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt", model.calculateTakt(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad,
            this.settings.totaltAntalArbetsdagarInnevarandeManad), Cellstyle.Readonly, percentSettings);

        let grandtotal = 0;
        periods.forEach(period => {
            const total = model.sumPlan(period);
            grandtotal += total;
            const isActive = period.isWithin(model.periodFrom, model.periodTom);
            const style = isActive ? 0 : Cellstyle.Readonly;

            row.addNumeric(period.key, total, style, change => this.onUpdateForsorjningsradPlan(model, leverantorsorderradId, period, change));
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addNumeric("transporteratleveransrad", model.sumTransporteratPlanerat(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.overenskommet, null, change => this.onUpdateOverenskommenKvantitet(model, leverantorsorderradId, change));
        row.addNumeric("avvikelse", model.avvikelse(periods), Cellstyle.Readonly);
    };

    private readonly buildForsorjningsradTransporteratrad = (row: HotRow, model: ForsorjningskallaAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.White | Cellstyle.NoRightBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoBottomBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT"), Cellstyle.Readonly | Cellstyle.White);

        let grandtotal = 0;
        periods.forEach(period => {
            const total = model.sumTransporterat(period);
            grandtotal += total;
            row.addNumeric(period.key, total, Cellstyle.Readonly);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("overenskommet", "", Cellstyle.Readonly);
    };

    private readonly buildLeverantorsorderradBalansrad = (row: HotRow, model: LeverantorsorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_BALANS"), Cellstyle.Readonly);

        let grandtotal = 0;
        periods.forEach(period => {
            const balans = model.sumBalans(period);
            grandtotal += balans;

            const style = Cellstyle.Readonly | (period.isCurrentlyActive ? Cellstyle.ActivePeriod : 0);
            row.addNumeric(period.key, balans, style);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly onUpdateLeveransradKvantitet = (change: NumericCellChangeArg, model: LeverantorsorderradAggregat, period: IAggregatPeriod) => {
        const periodStart = period.getStartOrLater(model.periodFrom);
        const periodEnd = period.getEndOrEarlier(model.periodTom);

        this.fordelaKvantitetService
            .getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateLeveransradKvantitet(result);

                const command = new SetLevorderLeveransradKvantitetCommand(c => {
                    c.leverantorsorderradId = model.leverantorsorderradId;
                    c.periodStart = periodStart;
                    c.periodEnd = periodEnd;
                    c.fordeladKvantitet = result;
                });
                this.commandService.addTransactionCommand(command);

                change.complete();
            });
    };

    private readonly buildLeverantorsorderradLeveransrad = (row: HotRow, expander: Expander, model: LeverantorsorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addExpander(expander, Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.AlignLeft, { rowSpan: 3 });
        row.add("rowHeader1", LeverantorsorderradCell.create(model));
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_LEVERANSRAD"), Cellstyle.Readonly | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt", model.calculateTaktLeverans(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad,
            this.settings.totaltAntalArbetsdagarInnevarandeManad), Cellstyle.Readonly, percentSettings);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumLeveransrad = model.sumLeveransrad(period);
            grandtotal += sumLeveransrad;

            const isActive = period.isWithin(model.periodFrom, model.periodTom);
            const style = isActive ? Cellstyle.Default : Cellstyle.Readonly;
            row.addNumeric(period.key, sumLeveransrad, style, change => this.onUpdateLeveransradKvantitet(change, model, period));
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addNumeric("overenskommet", model.sumOverenskommet(), Cellstyle.Readonly);
        row.addNumeric("transporteratleveransrad", model.transporteratLeveransrad(periods), Cellstyle.Readonly);
        row.addNumeric("avvikelse", model.avvikelse(periods), Cellstyle.Readonly);
    };

    private readonly buildLeverantorsorderradTransporteratrad = (row: HotRow, model: LeverantorsorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT"), Cellstyle.Readonly | Cellstyle.NoBottomBorder);

        let grandtotal = 0;
        periods.forEach(period => {
            const transporterat = model.sumTransporterat(period);
            grandtotal += transporterat;

            row.addNumeric(period.key, transporterat, Cellstyle.Readonly);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildSummaBalans = (row: HotRow, model: LeverantorsorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoBottomBorder | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_BALANS"), Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoBottomBorder);

        let grandtotal = 0;
        periods.forEach(period => {
            const balans = model.sumBalans(period);
            grandtotal += balans;

            const style = Cellstyle.Readonly | Cellstyle.Bold | (period.isCurrentlyActive ? Cellstyle.ActivePeriod : 0);
            row.addNumeric(period.key, balans, style);
        });
        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildSummaLeverans = (row: HotRow, model: LeverantorsorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addText("rowHeader1", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA"),
            Cellstyle.Readonly | Cellstyle.Uppercase | Cellstyle.Bold | Cellstyle.NoBottomBorder | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_LEVERANS"),
            Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoBottomBorder | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt", model.calculateTaktLeverans(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad,
            this.settings.totaltAntalArbetsdagarInnevarandeManad), Cellstyle.Readonly, percentSettings);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumLeveransrad = model.sumLeveransrad(period);
            grandtotal += sumLeveransrad;
            row.addNumeric(period.key, sumLeveransrad, Cellstyle.Readonly | Cellstyle.Bold);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addNumeric("transporteratleveransrad", model.sumTransporteratLeveransrad(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.sumOverenskommet(), Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly buildSummaPlan = (row: HotRow, model: LeverantorsorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoBottomBorder | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_PLAN"), Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt", model.calculateTakt(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad,
            this.settings.totaltAntalArbetsdagarInnevarandeManad), Cellstyle.Readonly, percentSettings);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumPeriod = model.sumPlan(period);
            grandtotal += sumPeriod;
            row.addNumeric(period.key, sumPeriod, Cellstyle.Readonly | Cellstyle.Bold);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildSummaTransporterat = (row: HotRow, model: LeverantorsorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_TRANSPORTERAT"), Cellstyle.Readonly | Cellstyle.Bold);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumPeriod = model.sumTransporterat(period);
            grandtotal += sumPeriod;
            row.addNumeric(period.key, sumPeriod, Cellstyle.Readonly | Cellstyle.Bold);
        });
        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildHotDoc = (): HotDoc => {
        const hotDocSettings: IHotDocSettings = {
            rowHeights: 40,
            raskSettings: {
                expander: {
                    col: 0,
                    name: "expanders"
                },
                calculateSum: true
            },
            fixedColumnsStart: 3
        };

        const colFactory = () => this.buildColumnDefinitions(this.aggregatePeriods);

        const rowFactory = (doc: HotDoc) => {
            const model = this.model;
            const periods = this.aggregatePeriods;

            model.leverantorsorderrader.forEach(leverantorsorderrad => {
                const expander = doc.createExpander(leverantorsorderrad.leverantorsorderradId);

                doc.createRow(row => this.buildLeverantorsorderradLeveransrad(row, expander, leverantorsorderrad, periods), Cellstyle.StrongTop);
                doc.createRow(row => this.buildLeverantorsorderradTransporteratrad(row, leverantorsorderrad, periods));
                doc.createRow(row => this.buildLeverantorsorderradBalansrad(row, leverantorsorderrad, periods));

                if (expander.expanded) {
                    leverantorsorderrad.forsorjningskallor.forEach(forsorjningskalla => {

                        doc.createRow(row => this.buildForsorjningsradPlanrad(row, forsorjningskalla, leverantorsorderrad.leverantorsorderradId, periods));

                        doc.createRow(row => this.buildForsorjningsradTransporteratrad(row, forsorjningskalla, periods));
                    });
                }
            });

            doc.createRow(row => this.buildSummaLeverans(row, model, periods), Cellstyle.StrongTop);
            doc.createRow(row => this.buildSummaBalans(row, model, periods));
            doc.createRow(row => this.buildSummaPlan(row, model, periods));
            doc.createRow(row => this.buildSummaTransporterat(row, model, periods));
        };

        return new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    };

    private prepareHotDoc(res: IOrderLeverantorsorderSearchResponse) {
        this.aggregatePeriods = AggregatPeriods.create("sv-SE", this.period);
        this.currentPeriod = this.aggregatePeriods.find(a => a.isCurrentlyActive) || null;
        this.model = new LeverantorsorderAggregat(res.leverantorsorderrader);
    }
}
